import _ from "lodash";
import Button from "../../Button/Button";
import Skeleton from "./Skeleton";
import { MinusIcon, PlusIcon } from "@heroicons/react/24/outline";
import { formatRupiah, toastNotify } from "../../../utils/Utils";
import { ScrollShadow, Tab, Tabs } from "@nextui-org/react";
import { useCallback, useEffect, useMemo } from "react";
import useWindowSize from "../../../hooks/useWindowSize";
import { useTranslation } from "react-i18next";

const ModalChild = ({
  step,
  setStep,
  tabs,
  selected,
  setSelected,
  isLoading,
  showModalLoading,
  mechanismData,
  getSettings,
  selectedProject,
  setSelectedMechanism,
  selectedMechanism,
  setSkuData,
  skuData,
}) => {
  const { t } = useTranslation();
  const { width } = useWindowSize();
  const discountAmount = (mechanism, parentheses = false) => {
    let text = null;

    if (
      _.isNull(mechanism.amountValue) &&
      _.isNull(mechanism.amountPoint) &&
      _.isNull(mechanism.amountPercentage)
    )
      return (text = "");

    if (mechanism.amountValue) {
      text = formatRupiah(mechanism?.amountValue?.toString(), "Rp. ");
    } else if (mechanism.amountPercentage) {
      text = `${mechanism?.amountPercentage}%`;
    } else if (mechanism.amountPoint) {
      text = `${mechanism.amountPoint} poin`;
    }
    return parentheses ? (text ? `(${text})` : "") : text;
  };

  const uvcQty = useCallback(
    (id) => {
      return skuData.find((sku) => sku.id === id)?.quantity || 0;
    },
    [skuData],
  );

  const getMaxQty = () => {
    const maxRedeemQty = selectedMechanism?.voucher?.settings?.maxRedeemQty;
    const uvcOpen = selectedMechanism?.voucher?.uvcOpen;

    let max = maxRedeemQty ?? 8;
    if (uvcOpen !== undefined && uvcOpen !== null) {
      max = Math.min(max, uvcOpen);
    }
    return max;
  };

  const totalQty = useMemo(() => {
    return skuData.reduce((sum, sku) => sum + (sku.quantity || 0), 0);
  }, [skuData]);

  const uvcQtyHandler = (item, increment = false) => {
    let newSkuData = [...skuData];
    const idx = newSkuData.findIndex((sku) => sku.id === item.id);
    if (idx !== -1) {
      const newQty = increment
        ? newSkuData[idx].quantity + 1
        : newSkuData[idx].quantity - 1;
      if (newQty > 0) {
        newSkuData[idx] = {
          ...newSkuData[idx],
          quantity: newQty,
        };
      } else {
        // remove the object if quantity is 0
        newSkuData.splice(idx, 1);
      }
    } else if (increment) {
      newSkuData.push({
        ...item,
        quantity: 1,
      });
    }
    setSkuData(newSkuData);
  };

  return (
    <>
      {step === 1 && (
        <div className="">
          <Tabs
            fullWidth
            size="md"
            aria-label="Tabs form"
            items={tabs}
            selectedKey={selected}
            onSelectionChange={setSelected}
            classNames={{
              tabList: "bg-transparent-black-5 rounded-xl p-1.5",
              cursor: "!bg-monochrome-0 rounded-lg",
              tab: "py-5 text-monochrome-600 font-semibold",
            }}
          >
            {(item) => (
              <Tab
                key={item.id}
                title={
                  <div className="flex items-center space-x-2">
                    <span>{item.label}</span>
                  </div>
                }
                className="px-0 focus:outline-none"
              >
                <div className="py-2">{item.content}</div>
              </Tab>
            )}
          </Tabs>
        </div>
      )}
      {step === 2 && (
        <>
          {isLoading ? (
            <Skeleton />
          ) : (
            <div className="flex flex-col gap-3">
              <p className="mb-1 text-xs text-monochrome-300">
                {t("redeem.selectMechanism")}
              </p>
              <ScrollShadow className="h-[600px] space-y-3">
                {!_.isEmpty(mechanismData) ? (
                  mechanismData.map((item, i) => (
                    <button
                      key={i}
                      className="flex w-full items-center gap-4 text-start"
                      onClick={() => {
                        if (_.isEmpty(item.skus))
                          return toastNotify(
                            "Tidak ada SKU pada mekanisme ini",
                            "error",
                          );

                        // reset skuData state
                        setSkuData([]);

                        // if (item.skus.length === 1) {
                        //   getSettings(
                        //     selectedProject.id,
                        //     item,
                        //     _.first(item.skus),
                        //   );
                        // } else {
                        setSelectedMechanism({
                          skus: item.skus,
                          voucher: item,
                        });
                        setStep(3);
                        // }
                      }}
                    >
                      {item.voucherImage ? (
                        <img
                          src={item.voucherImage}
                          alt="V"
                          className="h-10 min-h-10 w-10 min-w-10 rounded-lg border border-monochrome-50 object-cover"
                        />
                      ) : (
                        <div className="h-10 min-h-10 w-10 min-w-10 rounded-lg bg-monochrome-50"></div>
                      )}
                      <div className="col-span-4 flex h-full flex-col justify-center">
                        <p className="font-semibold">
                          {`${item.mechanism}`}{" "}
                          <span className="text-xs font-normal text-monochrome-500">{` ${discountAmount(item, true)}`}</span>
                        </p>
                        <p className="text-[10px] text-monochrome-200">
                          {_.isArray(item.skus)
                            ? item.skus.map((sku) => sku?.name).toString()
                            : "-"}
                        </p>
                      </div>
                    </button>
                  ))
                ) : (
                  <div className="flex flex-col items-center gap-4">
                    <svg
                      width="60"
                      height="60"
                      viewBox="0 0 23 23"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="text-monochrome-200"
                    >
                      <path
                        d="M10.5 8V7M10.5 12.5V11.5M10.5 17V16M5.7 4H19.3C20.4201 4 20.9802 4 21.408 4.21799C21.7843 4.40973 22.0903 4.71569 22.282 5.09202C22.5 5.51984 22.5 6.0799 22.5 7.2V8.5C20.567 8.5 19 10.067 19 12C19 13.933 20.567 15.5 22.5 15.5V16.8C22.5 17.9201 22.5 18.4802 22.282 18.908C22.0903 19.2843 21.7843 19.5903 21.408 19.782C20.9802 20 20.4201 20 19.3 20H5.7C4.5799 20 4.01984 20 3.59202 19.782C3.21569 19.5903 2.90973 19.2843 2.71799 18.908C2.5 18.4802 2.5 17.9201 2.5 16.8V15.5C4.433 15.5 6 13.933 6 12C6 10.067 4.433 8.5 2.5 8.5V7.2C2.5 6.0799 2.5 5.51984 2.71799 5.09202C2.90973 4.71569 3.21569 4.40973 3.59202 4.21799C4.01984 4 4.5799 4 5.7 4Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex flex-col items-center justify-center">
                      <p className="text-center font-semibold text-monochrome-200">
                        Tidak ada mekanisme voucher yang tersedia
                      </p>
                    </div>
                  </div>
                )}
              </ScrollShadow>
            </div>
          )}
        </>
      )}
      {step === 3 && (
        <>
          {isLoading ? (
            <Skeleton />
          ) : (
            <>
              <div className="flex max-h-[500px] flex-col gap-3 overflow-y-scroll">
                <p className="mb-1 text-xs text-monochrome-300">
                  {t("redeem.selectSku")}
                </p>
                {!_.isEmpty(selectedMechanism.skus) ? (
                  selectedMechanism.skus.map((item, i) => (
                    <div
                      key={i}
                      className={`flex w-full items-center justify-between gap-4 text-start`}
                      onClick={() => {
                        if (
                          selectedMechanism.voucher.voucherMethod !== "DYNAMIC"
                        )
                          return;
                        const newSku = [{ ...item, quantity: 1 }];
                        getSettings(
                          selectedProject.id,
                          selectedMechanism.voucher,
                          newSku,
                        );
                      }}
                    >
                      <div className="flex w-full items-center gap-4 text-start">
                        <div className="h-10 min-h-10 w-10 min-w-10 rounded-lg bg-monochrome-50"></div>
                        <div className="col-span-4 flex h-full flex-col justify-between">
                          <p className="font-semibold">{item.name}</p>
                          <p className="text-[10px] text-monochrome-200">
                            {discountAmount(selectedMechanism.voucher)}
                          </p>
                        </div>
                      </div>
                      {selectedMechanism.voucher.voucherMethod !==
                        "DYNAMIC" && (
                        <div className="flex items-center gap-2">
                          {/* Decrement */}
                          <button
                            className="py-auto flex h-6 w-6 items-center justify-center rounded-md border bg-blue-600 text-lg font-semibold  text-white transition-colors disabled:bg-monochrome-50 disabled:text-monochrome-300"
                            onClick={() => uvcQtyHandler(item, false)}
                            disabled={uvcQty(item.id) === 0}
                          >
                            <MinusIcon className="h-3 w-3 stroke-[4]" />
                          </button>
                          {/* Value */}
                          <p>{uvcQty(item.id)}</p>
                          {/* Increment */}
                          <button
                            className="py-auto flex h-6 w-6 items-center justify-center rounded-md border bg-blue-600 text-lg font-semibold  text-white transition-colors disabled:bg-monochrome-50 disabled:text-monochrome-300"
                            onClick={() => uvcQtyHandler(item, true)}
                            disabled={totalQty >= getMaxQty()}
                          >
                            <PlusIcon className="h-3 w-3 stroke-[4]" />
                          </button>
                        </div>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="flex flex-col items-center gap-4">
                    <svg
                      width="60"
                      height="60"
                      viewBox="0 0 23 23"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                      className="text-monochrome-200"
                    >
                      <path
                        d="M10.5 8V7M10.5 12.5V11.5M10.5 17V16M5.7 4H19.3C20.4201 4 20.9802 4 21.408 4.21799C21.7843 4.40973 22.0903 4.71569 22.282 5.09202C22.5 5.51984 22.5 6.0799 22.5 7.2V8.5C20.567 8.5 19 10.067 19 12C19 13.933 20.567 15.5 22.5 15.5V16.8C22.5 17.9201 22.5 18.4802 22.282 18.908C22.0903 19.2843 21.7843 19.5903 21.408 19.782C20.9802 20 20.4201 20 19.3 20H5.7C4.5799 20 4.01984 20 3.59202 19.782C3.21569 19.5903 2.90973 19.2843 2.71799 18.908C2.5 18.4802 2.5 17.9201 2.5 16.8V15.5C4.433 15.5 6 13.933 6 12C6 10.067 4.433 8.5 2.5 8.5V7.2C2.5 6.0799 2.5 5.51984 2.71799 5.09202C2.90973 4.71569 3.21569 4.40973 3.59202 4.21799C4.01984 4 4.5799 4 5.7 4Z"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    <div className="flex flex-col items-center justify-center">
                      <p className="text-center font-semibold text-monochrome-200">
                        Tidak ada SKU yang tersedia
                      </p>
                    </div>
                  </div>
                )}
              </div>
              {selectedMechanism.voucher.voucherMethod !== "DYNAMIC" &&
                width < 768 && (
                  <div className="fixed bottom-0 left-1/2 z-10 flex w-full max-w-full -translate-x-1/2 items-center justify-center bg-white px-5 py-4">
                    <Button
                      label={t("redeem.confirmVoucherRedemption")}
                      variant="gradient"
                      disabled={_.isEmpty(skuData) || showModalLoading}
                      onClick={() => {
                        getSettings(
                          selectedProject.id,
                          selectedMechanism.voucher,
                        );
                      }}
                      fullWidth
                    />
                  </div>
                )}
            </>
          )}
        </>
      )}
    </>
  );
};

export default ModalChild;
